import React, { useState, useEffect } from "react";
import { Box, Paper, LinearProgress } from "@mui/material";
import { DataGrid, GridRowModel, GridColDef } from "@mui/x-data-grid";
import { useSearchParams } from "react-router-dom";
import getProspects from "api/canprospect/getProspects";
import getProspectStatuses from "api/canprospect/getProspectStatuses";
import { ProspectInterface } from "api/canprospect/prospect";
import updateProspect from "api/canprospect/updateProspect";
import deleteProspect from "api/canprospect/deleteProspect";
import { getProspectTableColumns } from "./prospectTableColumns";
import DeleteDialog from "./DeleteDialog";
import ToastSnackbar from "./ToastSnackbar";
import ProspectDetailPanel from "./ProspectDetailPanel";
import useProspectDetail from "./hooks/useProspectDetail";

interface ProspectTableProps {
  reload: boolean;
}

const ProspectTable: React.FC<ProspectTableProps> = ({ reload }) => {
  // Get URL parameters and use them as initial state
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = parseInt(searchParams.get("page") || "0", 10);
  const initialSearch = searchParams.get("search") || "";

  const [prospects, setProspects] = useState<ProspectInterface[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(initialPage); // Zero-based for DataGrid
  const [loading, setLoading] = useState<boolean>(false);
  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [statuses, setStatuses] = useState<string[]>([]);
  const [updating, setUpdating] = useState<boolean>(false);
  const {
    open: detailOpen,
    prospect: detailProspect,
    loading: detailLoading,
    error: detailError,
    activeTab,
    setActiveTab,
    openPanel,
    closePanel,
    refetch: refetchDetail,
  } = useProspectDetail();
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [prospectToDelete, setProspectToDelete] = useState<number | null>(null);
  const [searchKeyword, setSearchKeyword] = useState<string>(initialSearch);

  const pageSize = 50; // Rows per page

  useEffect(() => {
    setSearchParams({
      page: page.toString(),
      search: searchKeyword,
    });
  }, [page, searchKeyword, setSearchParams]);

  useEffect(() => {
    fetchProspects(page + 1, searchKeyword);
  }, [page, searchKeyword]);

  const fetchProspects = async (pageNumber: number, keyword: string = "") => {
    setLoading(true);
    try {
      // Note: pageNumber is 1-based for the backend.
      const { rows, total } = await getProspects(pageNumber, pageSize, keyword);
      setProspects(rows);
      setTotal(total);
    } catch (error) {
      console.error("Failed to fetch prospects:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const { statuses } = await getProspectStatuses();
        setStatuses(statuses.map((status) => status.Status));
      } catch (error) {
        console.error("Error fetching statuses:", error);
      }
    };
    fetchStatuses();
  }, []);

  // If reload is triggered, reset the page to 0
  useEffect(() => {
    if (reload) {
      setPage(0);
    }
  }, [reload]);

  const handleProcessRowUpdate = async (
    newRow: GridRowModel,
    oldRow: GridRowModel
  ) => {
    setUpdating(true);
    try {
      const updatedProspects = prospects.map((row) =>
        row.Id === newRow.Id ? (newRow as ProspectInterface) : row
      );
      setProspects(updatedProspects);

      await updateProspect(newRow as ProspectInterface);

      setToastMessage(`Row ${newRow.Id} updated successfully!`);
      setToastOpen(true);

      return newRow;
    } catch (error) {
      console.error("Failed to update prospect:", error);
      setToastMessage(`Failed to update row ${newRow.Id}.`);
      setToastOpen(true);
      return oldRow;
    } finally {
      setUpdating(false);
    }
  };

  const handleNavigate = (id: number | null | undefined) => {
    if (id) {
      openPanel(id);
    }
  };

  const handleDetailClose = () => {
    closePanel();
    // Refresh prospect list in case edits were made
    fetchProspects(page + 1, searchKeyword);
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  const openDeleteModal = (id: number) => {
    setProspectToDelete(id);
    setDeleteModalOpen(true);
  };

  const columns: GridColDef[] = getProspectTableColumns({
    statuses,
    handleNavigate,
    openDeleteModal,
  });

  const handleDelete = async () => {
    if (prospectToDelete === null) return;
    try {
      await deleteProspect(prospectToDelete);
      setToastMessage(
        `Prospect with ID = ${prospectToDelete} deleted successfully!`
      );
      setToastOpen(true);
      setProspects((prevProspects) =>
        prevProspects.filter((prospect) => prospect.Id !== prospectToDelete)
      );
    } catch (error) {
      console.error("Failed to delete prospect:", error);
      setToastMessage(
        `Failed to delete prospect with ID = ${prospectToDelete}.`
      );
      setToastOpen(true);
    } finally {
      setDeleteModalOpen(false);
      setProspectToDelete(null);
    }
  };

  return (
    <Box style={{ height: "80vh", width: "90%", margin: "auto" }}>
      <Paper elevation={3} style={{ height: "100%", backgroundColor: "#fff" }}>
        <DataGrid
          page={page}
          rows={prospects}
          columns={columns}
          rowHeight={40}
          getRowId={(row) => row.Id}
          pageSize={pageSize}
          rowCount={total}
          loading={loading}
          pagination
          paginationMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          rowsPerPageOptions={[pageSize]}
          checkboxSelection={false}
          processRowUpdate={handleProcessRowUpdate}
          experimentalFeatures={{ newEditingApi: true }}
          filterMode="server"
          onFilterModelChange={(model) => {
            // Combine all filter values into a single search keyword
            const combinedKeyword = model.items
              .filter((item) => item.value)
              .map((item) => item.value)
              .join(" ");
            setSearchKeyword(combinedKeyword);
          }}
          sx={{
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(72, 255, 72, 0.1)",
            },
          }}
        />
        {updating && <LinearProgress style={{ width: "100%" }} />}
      </Paper>

      <DeleteDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={handleDelete}
      />

      <ToastSnackbar
        open={toastOpen}
        onClose={handleToastClose}
        message={toastMessage}
        severity="success"
      />

      <ProspectDetailPanel
        open={detailOpen}
        onClose={handleDetailClose}
        prospect={detailProspect}
        loading={detailLoading}
        error={detailError}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onRefetch={refetchDetail}
      />
    </Box>
  );
};

export default ProspectTable;